import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaBriefcase, FaShoppingCart, FaGraduationCap } from 'react-icons/fa';

const FeaturedServices = () => {
  const services = [
    {
      id: 'portfolio',
      title: 'Portfolio Websites',
      description: 'Showcase your work with stunning visual presentations and smooth interactions.',
      icon: <FaBriefcase className="text-4xl text-cyber-pink" />,
      color: 'cyber-pink',
      link: '/services/portfolio'
    },
    {
      id: 'ecommerce',
      title: 'E-Commerce Stores',
      description: 'Sell products online with secure, user-friendly e-commerce platforms.',
      icon: <FaShoppingCart className="text-4xl text-cyber-neon" />,
      color: 'cyber-neon',
      link: '/services/ecommerce'
    },
    {
      id: 'educational',
      title: 'Educational Platforms',
      description: 'Create engaging learning experiences with custom educational websites.',
      icon: <FaGraduationCap className="text-4xl text-cyber-purple" />,
      color: 'cyber-purple',
      link: '/services/educational'
    }
  ];

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <div className="py-24 bg-cyber-dark relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="h-full w-full bg-[linear-gradient(90deg,#9D4EDD15_1px,transparent_1px),linear-gradient(0deg,#9D4EDD15_1px,transparent_1px)] bg-[size:30px_30px]"></div>
      </div>
      
      {/* Diagonal line effect */}
      <div className="absolute -right-20 top-0 bottom-0 w-40 bg-gradient-to-l from-cyber-pink/10 to-transparent transform -skew-x-12"></div>
      <div className="absolute -left-20 bottom-0 top-0 w-40 bg-gradient-to-r from-cyber-neon/10 to-transparent transform -skew-x-12"></div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8, 
            ease: [0.22, 1, 0.36, 1] 
          }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-orbitron font-bold">
            Our <span className="text-cyber-neon">Featured</span> Services
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto mt-4 text-lg">
            We create cutting-edge digital experiences with advanced technology and stunning design
          </p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              variants={itemVariants}
              whileHover={{ 
                y: -10,
                transition: { duration: 0.3 }
              }}
              className={`cyber-card p-8 border border-${service.color}/50 h-full group hover:shadow-lg hover:shadow-${service.color}/20 transition-all duration-500`}
            >
              <div className={`w-16 h-16 rounded-full bg-${service.color}/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-700`}>
                {service.icon}
              </div>
              
              <h3 className={`text-2xl font-orbitron font-bold mb-4 text-white group-hover:text-${service.color} transition-colors duration-300`}>
                {service.title}
              </h3>
              
              <p className="text-gray-300 mb-6">
                {service.description}
              </p>
              
              <Link 
                to={service.link}
                className={`inline-flex items-center text-${service.color} font-medium group-hover:translate-x-2 transition-transform duration-300`}
              >
                <span>Explore Service</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <Link 
            to="/services"
            className="cyber-button"
          >
            <span>View All Services</span>
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default FeaturedServices; 